import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ProjectCard from './components/ProjectCard';
import FilterControls from './components/FilterControls';
import RecentAchievements from './components/RecentAchievements';
import NoticeSection from './components/NoticeSection';
import EmptyState from './components/EmptyState';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { getCachedSignedUrl } from '../../utils/storage';
import Seo from '../../components/Seo';
import ProofHeader from '../proof-detail-view/components/ProofHeader';
import ProofImageViewer from '../proof-detail-view/components/ProofImageViewer';
import ProofMetadata from '../proof-detail-view/components/ProofMetadata';
import ProofLikeButton from '../proof-detail-view/components/ProofLikeButton';

const ProjectDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    sort: 'recent'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recentAchievements, setRecentAchievements] = useState([]);
  const [recentNotices, setRecentNotices] = useState([]);

  const [isProofDetailOpen, setIsProofDetailOpen] = useState(false);
  const [activeProofIndex, setActiveProofIndex] = useState(0);

  const openProofDetailById = useCallback(
    (proofId) => {
      const idx = recentAchievements?.findIndex((p) => p?.id === proofId);
      if (idx < 0) return;
      setActiveProofIndex(idx);
      setIsProofDetailOpen(true);
    },
    [recentAchievements]
  );


  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;

      setIsLoading(true);
      const { data, error } = await supabase.rpc('ch_list_projects_with_stats');

      if (error) {
        setProjects([]);
        setIsLoading(false);
        return;
      }

      const next = await Promise.all(
        (data || []).map(async (p) => {
          const coverUrl = p?.cover_image_path
            ? await getCachedSignedUrl({ bucket: 'project-covers', path: p.cover_image_path })
            : '';

          return {
            id: p.id,
            title: p.title,
            description: p.description,
            coverImage: coverUrl,
            coverImageAlt: p.cover_image_alt || p.title,
            coverImagePath: p.cover_image_path || '',
            totalChallenges: p.total_challenges ?? 0,
            completedChallenges: p.completed_challenges ?? 0,
            completedProofs: p.completed_proofs ?? 0,
            totalLikes: p.total_likes ?? 0,
            averageStars: Number(p.average_stars ?? 0),
            isArchived: p.is_archived ?? false,
            createdAt: p.created_at,
            status: p.status || 'active',
          };
        })
      );

      setProjects(next);
      setIsLoading(false);
    };

    load();
  }, [user?.id]);

  useEffect(() => {
    const loadRecent = async () => {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('ch_proofs')
        .select('id, title, description, rating, submitted_at, image_path, youtube_url, challenge:ch_challenges(title, project:ch_projects(title))')
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false })
        .limit(6);

      if (error) {
        setRecentAchievements([]);
        return;
      }

      const getYoutubeVideoId = (rawUrl) => {
        const url = String(rawUrl || '').trim();
        if (!url) return '';
        const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
        if (directId) return directId;
        try {
          const u = new URL(url);
          const host = (u.hostname || '').replace(/^www\./, '');
          if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
          const v = u.searchParams.get('v');
          if (v) return v.slice(0, 11);
          const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
          if (m?.[1]) return String(m[1]).slice(0, 11);
          return '';
        } catch {
          return '';
        }
      };

      const getYoutubeThumb = (rawUrl) => {
        const id = getYoutubeVideoId(rawUrl);
        if (!id) return '';
        return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      };

      const mapped = await Promise.all(
        (data || []).map(async (p) => {
          const thumb = p.youtube_url ? getYoutubeThumb(p.youtube_url) : '';
          const url = p.image_path ? await getCachedSignedUrl({ bucket: 'proof-images', path: p.image_path }) : (thumb || '');
          return {
            id: p.id,
            title: p.challenge?.title || p.title || '증명',
            description: p.description || '',
            stars: Number(p.rating || 0),
            projectName: p.challenge?.project?.title || '',
            date: p.submitted_at ? String(p.submitted_at).slice(0, 10) : '',
            proofImage: url,
            proofImageAlt: p.challenge?.title ? `Proof image for ${p.challenge.title}` : 'Proof image',
            image: url,
            imageAlt: p.challenge?.title ? `Proof image for ${p.challenge.title}` : 'Proof image',
            youtubeUrl: p.youtube_url || '',
          };
        })
      );

      setRecentAchievements(mapped);
    };

    loadRecent();
  }, [user?.id]);

  useEffect(() => {
    const loadNotices = async () => {
      const { data, error } = await supabase
        .from('ch_notices')
        .select('id, title, author_name, created_at, files:ch_notice_files(file_path, content_type)')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) {
        setRecentNotices([]);
        return;
      }

      setRecentNotices(
        (data || []).map((n) => {
          const img = (n.files || []).find((f) => String(f?.content_type || '').startsWith('image/') && f?.file_path);
          const thumbnailUrl = img?.file_path
            ? supabase.storage.from('notice-files').getPublicUrl(img.file_path).data.publicUrl
            : '';
          return {
            id: n.id,
            title: n.title,
            authorName: n.author_name || '꿈현공장장',
            date: n.created_at ? String(n.created_at).slice(0, 10) : '',
            thumbnailUrl,
          };
        })
      );
    };

    loadNotices();
  }, []);

  useEffect(() => {
    let filtered = [...projects];

    if (searchQuery) {
      filtered = filtered?.filter((project) =>
      project?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      project?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    switch (filters?.sort) {
      case 'recent':
        filtered?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        filtered?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'progress':
        filtered?.sort((a, b) => (b?.completedProofs ?? 0) - (a?.completedProofs ?? 0));
        break;
      case 'alphabetical':
        filtered?.sort((a, b) => a?.title?.localeCompare(b?.title, 'ko'));
        break;
      default:
        break;
    }

    setFilteredProjects(filtered);
  }, [filters, searchQuery, projects]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="목표 관리 - 도전 플랜 | 꿈현.Labs" canonicalPath="/project-dashboard" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="pt-28 lg:pt-28 pb-20 md:pb-24 lg:pb-16 px-4 sm:px-5 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto space-y-6 md:space-y-8">
          <div className="pt-3 md:pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                목표 관리
              </h1>
            </div>

            <button
              type="button"
              onClick={() => navigate('/project-creation')}
              className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-md shadow-gaming-md transition-gaming hover:shadow-gaming-lg active:translate-y-px"
            >
              <span className="text-sm md:text-base font-medium">새 목표</span>
            </button>
          </div>

          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            onSearchChange={setSearchQuery}
            searchQuery={searchQuery} />


          {isLoading ? (
            <div className="bg-card rounded-lg shadow-gaming-md p-6">
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            </div>
          ) : (

            <>
              {filteredProjects?.length === 0 && searchQuery === '' ? (
                <EmptyState type="no-projects" />
              ) : filteredProjects?.length === 0 ? (
                <EmptyState type="no-results" />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                  {filteredProjects?.map((project) => (
                    <ProjectCard key={project?.id} project={project} />
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <RecentAchievements achievements={(recentAchievements || []).slice(0, 5)} onOpenDetail={openProofDetailById} />

                <div>
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">공지사항</h2>
                    <button
                      type="button"
                      onClick={() => navigate('/notices')}
                      className="px-4 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
                    >
                      전체 보기
                    </button>
                  </div>
                  <NoticeSection notices={(recentNotices || []).slice(0, 5)} hideHeader />
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {isProofDetailOpen && recentAchievements?.[activeProofIndex] && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 flex items-center justify-center p-4"
          onClick={() => setIsProofDetailOpen(false)}
        >
          <div className="w-full max-w-4xl" onClick={(e) => e?.stopPropagation()}>
            <div className="bg-card rounded-lg shadow-gaming-lg p-4 md:p-6 lg:p-8 max-h-[85vh] overflow-y-auto">
              <ProofHeader
                currentProof={activeProofIndex + 1}
                totalProofs={recentAchievements?.length}
                challengeTitle={recentAchievements?.[activeProofIndex]?.title}
                onClose={() => setIsProofDetailOpen(false)}
              />

              <div className="space-y-6 md:space-y-8">
                <ProofImageViewer
                  imageUrl={recentAchievements?.[activeProofIndex]?.image}
                  imageAlt={recentAchievements?.[activeProofIndex]?.imageAlt}
                  onPrevious={() => setActiveProofIndex((v) => Math.max(0, v - 1))}
                  onNext={() => setActiveProofIndex((v) => Math.min((recentAchievements?.length || 1) - 1, v + 1))}
                  hasPrevious={activeProofIndex > 0}
                  hasNext={activeProofIndex < (recentAchievements?.length || 0) - 1}
                />

                <ProofMetadata
                  submissionDate={recentAchievements?.[activeProofIndex]?.date || ''}
                  starRating={recentAchievements?.[activeProofIndex]?.stars || 0}
                  description={recentAchievements?.[activeProofIndex]?.description || ''}
                  youtubeUrl={recentAchievements?.[activeProofIndex]?.youtubeUrl || ''}
                />

                {recentAchievements?.[activeProofIndex]?.id ? (
                  <div className="flex justify-end">
                    <ProofLikeButton proofId={recentAchievements?.[activeProofIndex]?.id} />
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsProofDetailOpen(false)}
                    className="px-4 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>);

};

export default ProjectDashboard;